import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Upload, Trash2, Star, GripVertical, Image as ImageIcon,
  Loader2, AlertCircle, Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const MAX_PROPERTY_IMAGES = 10;
const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];

/**
 * PhotoManager — unified photo management for property images.
 * Works for admin, agent, owner, master-partner backed by the unified
 * /api/properties/{id}/images endpoints.
 *
 * Props:
 *  - propertyId: string
 *  - images: string[]           (current property.images array)
 *  - onChange?: (newImages) => void   (called whenever images change)
 *  - readOnly?: boolean
 *  - maxImages?: number         (default 10)
 */
export const PhotoManager = ({
  propertyId,
  images = [],
  onChange,
  readOnly = false,
  maxImages = MAX_PROPERTY_IMAGES,
}) => {
  const [localImages, setLocalImages] = useState(images);
  const [uploadingFiles, setUploadingFiles] = useState([]); // [{id, name, progress, error}]
  const [busyAction, setBusyAction] = useState(null); // 'reorder' | 'delete' | 'cover'
  const fileInputRef = useRef(null);

  // Keep local images in sync if parent updates them
  React.useEffect(() => {
    setLocalImages(images);
  }, [images]);

  const emit = (next) => {
    setLocalImages(next);
    if (onChange) onChange(next);
  };

  const handleFiles = useCallback(async (fileList) => {
    if (!propertyId) {
      toast.error("Guarda primero la propiedad antes de subir fotos");
      return;
    }
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const room = maxImages - localImages.length - uploadingFiles.length;
    if (room <= 0) {
      toast.error(`Ya alcanzaste el máximo de ${maxImages} fotos`);
      return;
    }
    const accepted = [];
    const rejected = [];
    for (const f of files) {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        rejected.push({ name: f.name, reason: "formato no soportado" });
        continue;
      }
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        rejected.push({ name: f.name, reason: `mayor a ${MAX_FILE_SIZE_MB}MB` });
        continue;
      }
      accepted.push(f);
    }
    if (rejected.length) {
      toast.error(`${rejected.length} archivo(s) rechazados: ${rejected.map((r) => `${r.name} (${r.reason})`).join(", ")}`);
    }
    const toUpload = accepted.slice(0, room);
    if (accepted.length > room) {
      toast.warning(`Solo se subirán ${room} archivos para no exceder el máximo de ${maxImages}.`);
    }

    // Upload sequentially to avoid hammering the backend
    for (const file of toUpload) {
      const ticketId = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 6)}`;
      setUploadingFiles((prev) => [
        ...prev,
        { id: ticketId, name: file.name, progress: 0, preview: URL.createObjectURL(file) },
      ]);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const resp = await axios.post(`${API}/properties/${propertyId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
            setUploadingFiles((prev) =>
              prev.map((u) => (u.id === ticketId ? { ...u, progress: pct } : u))
            );
          },
        });
        const newImages = resp.data?.images;
        if (Array.isArray(newImages)) emit(newImages);
        setUploadingFiles((prev) => prev.filter((u) => u.id !== ticketId));
      } catch (err) {
        const msg = err?.response?.data?.detail || `No se pudo subir ${file.name}`;
        toast.error(msg);
        setUploadingFiles((prev) =>
          prev.map((u) => (u.id === ticketId ? { ...u, error: msg, progress: 100 } : u))
        );
        // Remove failed entry after a moment so user sees the error
        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((u) => u.id !== ticketId));
        }, 4000);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [propertyId, localImages.length, uploadingFiles.length, maxImages]); // eslint-disable-line

  const onDeleteImage = async (idx) => {
    if (!propertyId) return;
    setBusyAction("delete");
    try {
      const resp = await axios.delete(`${API}/properties/${propertyId}/images/${idx}`);
      const newImages = resp.data?.images;
      if (Array.isArray(newImages)) emit(newImages);
      toast.success("Foto eliminada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar la foto");
    } finally {
      setBusyAction(null);
    }
  };

  const onSetCover = async (idx) => {
    if (idx === 0 || !propertyId) return;
    setBusyAction("cover");
    // Optimistic UI
    const optimistic = [localImages[idx], ...localImages.filter((_, i) => i !== idx)];
    emit(optimistic);
    try {
      const resp = await axios.put(`${API}/properties/${propertyId}/images`, { set_cover: idx });
      if (Array.isArray(resp.data?.images)) emit(resp.data.images);
      toast.success("Foto marcada como principal");
    } catch (err) {
      // Revert
      emit(localImages);
      toast.error(err?.response?.data?.detail || "No se pudo marcar como principal");
    } finally {
      setBusyAction(null);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const newImages = Array.from(localImages);
    const [moved] = newImages.splice(result.source.index, 1);
    newImages.splice(result.destination.index, 0, moved);
    emit(newImages); // optimistic
    setBusyAction("reorder");
    try {
      const resp = await axios.put(`${API}/properties/${propertyId}/images`, { images: newImages });
      if (Array.isArray(resp.data?.images)) emit(resp.data.images);
    } catch (err) {
      emit(localImages); // revert
      toast.error(err?.response?.data?.detail || "No se pudo reordenar");
    } finally {
      setBusyAction(null);
    }
  };

  const totalSlots = localImages.length + uploadingFiles.length;
  const canAddMore = !readOnly && totalSlots < maxImages;

  return (
    <div className="space-y-4" data-testid="photo-manager">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> Fotos de la propiedad
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">
            {localImages.length} de {maxImages} · Arrastra para reordenar · La primera foto es la principal
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(",")}
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="photo-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canAddMore}
              onClick={() => fileInputRef.current?.click()}
              data-testid="photo-add-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Agregar foto{canAddMore ? `s (${maxImages - totalSlots})` : ""}
            </Button>
          </div>
        )}
      </div>

      {/* Drop zone (drag from OS) */}
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          className={`border-2 border-dashed rounded-xl p-4 text-center text-xs transition-colors ${
            canAddMore ? "border-slate-300 bg-slate-50/50 hover:border-primary hover:bg-primary/5 cursor-pointer text-slate-500" : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
          }`}
          onClick={() => canAddMore && fileInputRef.current?.click()}
          data-testid="photo-dropzone"
        >
          <Upload className="w-5 h-5 mx-auto mb-1 opacity-70" />
          {canAddMore
            ? "Arrastra fotos aquí o haz click para seleccionar (JPG, PNG, WebP, HEIC · máx 10MB)"
            : `Máximo ${maxImages} fotos alcanzado. Elimina alguna antes de agregar más.`}
        </div>
      )}

      {localImages.length === 0 && uploadingFiles.length === 0 ? (
        <div className="py-10 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">
          <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Aún no hay fotos cargadas
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="photos" direction="horizontal">
            {(droppable) => (
              <div
                {...droppable.droppableProps}
                ref={droppable.innerRef}
                className="flex flex-wrap gap-3"
              >
                {localImages.map((img, idx) => (
                  <Draggable key={`img-${idx}-${img.slice(-12)}`} draggableId={`img-${idx}-${img.slice(-12)}`} index={idx} isDragDisabled={readOnly || busyAction === "reorder"}>
                    {(draggable, snapshot) => (
                      <div
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                        className={`relative group rounded-xl overflow-hidden border-2 bg-slate-100 transition-shadow ${
                          idx === 0 ? "border-primary shadow-lg" : "border-transparent"
                        } ${snapshot.isDragging ? "shadow-2xl ring-2 ring-primary scale-105" : ""}`}
                        style={{
                          width: 132,
                          height: 132,
                          ...draggable.draggableProps.style,
                        }}
                        data-testid={`photo-tile-${idx}`}
                      >
                        <img
                          src={img}
                          alt={`Foto ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = "https://via.placeholder.com/132?text=Sin+vista+previa";
                          }}
                        />
                        {/* Cover badge */}
                        {idx === 0 && (
                          <div className="absolute top-1 left-1 bg-primary text-[#384451] text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" /> PORTADA
                          </div>
                        )}
                        {/* Drag handle */}
                        {!readOnly && (
                          <div
                            {...draggable.dragHandleProps}
                            className="absolute top-1 right-1 bg-black/50 backdrop-blur-sm text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                            title="Arrastrar para reordenar"
                          >
                            <GripVertical className="w-3.5 h-3.5" />
                          </div>
                        )}
                        {/* Bottom controls */}
                        {!readOnly && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                            {idx !== 0 ? (
                              <button
                                type="button"
                                onClick={() => onSetCover(idx)}
                                disabled={!!busyAction}
                                className="text-[10px] text-white bg-primary/90 hover:bg-primary px-2 py-1 rounded-md font-semibold flex items-center gap-1 disabled:opacity-50"
                                title="Marcar como principal"
                                data-testid={`photo-set-cover-${idx}`}
                              >
                                <Star className="w-3 h-3" /> Principal
                              </button>
                            ) : <span />}
                            <button
                              type="button"
                              onClick={() => onDeleteImage(idx)}
                              disabled={!!busyAction}
                              className="text-white bg-red-600/90 hover:bg-red-600 p-1 rounded-md disabled:opacity-50"
                              title="Eliminar foto"
                              data-testid={`photo-delete-${idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppable.placeholder}

                {/* Uploading thumbnails */}
                {uploadingFiles.map((u) => (
                  <div
                    key={u.id}
                    className="relative rounded-xl overflow-hidden border-2 border-dashed border-primary/40 bg-slate-100"
                    style={{ width: 132, height: 132 }}
                    data-testid={`photo-uploading-${u.id}`}
                  >
                    <img src={u.preview} alt="" className="w-full h-full object-cover opacity-60" />
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex flex-col items-center justify-center p-2 text-white">
                      {u.error ? (
                        <>
                          <AlertCircle className="w-6 h-6 text-red-300 mb-1" />
                          <p className="text-[10px] text-center leading-tight">{u.error}</p>
                        </>
                      ) : u.progress >= 100 ? (
                        <>
                          <Check className="w-6 h-6 text-emerald-300 mb-1" />
                          <p className="text-[10px]">Procesando...</p>
                        </>
                      ) : (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin mb-1.5" />
                          <p className="text-[10px] truncate w-full text-center">{u.name}</p>
                        </>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/30">
                      <div
                        className={`h-full transition-all ${u.error ? "bg-red-500" : "bg-primary"}`}
                        style={{ width: `${u.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {busyAction && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" />
          {busyAction === "reorder" ? "Guardando nuevo orden..." :
           busyAction === "delete"  ? "Eliminando..." :
           busyAction === "cover"   ? "Marcando como principal..." : "Procesando..."}
        </p>
      )}
    </div>
  );
};

export default PhotoManager;
